import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  CloseButton,
  createStyles,
  Divider,
  FileButton,
  Group,
  Modal,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useRef} from 'react';

import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import BenchlingConfig from 'BenchlingConfig';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';

import EventTriggerConfigForm from './EventTriggerConfigForm';
import RoutineAccessSection from './RoutineAccessSection';
import RoutineFieldHeader from './RoutineFieldHeader';
import {canSaveRoutine, isTriggerComplete} from './routineFormValidation';
import {
  AllowedEventTrigger,
  EventTriggerConfig,
  RoutineRunAs,
  RoutineStatus,
  TimeTriggerConfig,
  TriggerType,
} from './RoutineTypes';
import TimeTriggerConfigForm from './TimeTriggerConfigForm';
import TriggerTypeSelector from './TriggerTypeSelector';
import {AccessLevel, RoutineFormState} from './useRoutineForm';

interface RoutineFormProps {
  isEdit: boolean;
  formState: RoutineFormState;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onPromptChange: (value: string) => void;
  onRemoveFileBlock: (index: number) => void;
  onAddFile: (file: File) => void;
  isUploading: boolean;
  onTriggerTypeChange: (value: TriggerType) => void;
  onTriggerConfigChange: (updates: Partial<TimeTriggerConfig | EventTriggerConfig>) => void;
  onAccessLevelChange: (level: AccessLevel) => void;
  onAgentWorkspaceFolderChange: (folderId: string | null) => void;
  onMemoryEnabledChange: (enabled: boolean) => void;
  onRunAsChange: (value: RoutineRunAs) => void;
  isDirty: boolean;
  isSaving: boolean;
  error: string | null;
  onSave: () => void;
  onCancel: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onArchive?: () => void;
  onViewAuditLog?: () => void;
  currentStatus?: RoutineStatus;
  canArchive?: boolean;
  canEdit?: boolean;
  canPause?: boolean;
  canResume?: boolean;
  allowedEventTriggers: Array<AllowedEventTrigger>;
  timeBasedEnabled: boolean;
  eventBasedEnabled: boolean;
}

const useStyles = createStyles((theme) => ({
  form: {
    overflowY: 'auto',
    height: '100%',
  },
  fieldset: {
    border: 0,
    margin: 0,
    minWidth: 0,
    padding: 0,
  },
}));

export default function RoutineForm({
  isEdit,
  formState,
  onNameChange,
  onDescriptionChange,
  onPromptChange,
  onRemoveFileBlock,
  onAddFile,
  isUploading,
  onTriggerTypeChange,
  onTriggerConfigChange,
  onAccessLevelChange,
  onAgentWorkspaceFolderChange,
  onMemoryEnabledChange,
  onRunAsChange,
  isDirty,
  isSaving,
  error,
  onSave,
  onCancel,
  onPause,
  onResume,
  onArchive,
  onViewAuditLog,
  currentStatus,
  canArchive = false,
  canEdit = false,
  canPause = false,
  canResume = false,
  allowedEventTriggers,
  timeBasedEnabled,
  eventBasedEnabled,
}: RoutineFormProps): JSX.Element {
  const {classes} = useStyles();
  const [archiveModalOpened, {open: openArchiveModal, close: closeArchiveModal}] = useDisclosure(false);
  const resetFileInputRef = useRef<() => void>(null);

  // Full access has three states: hidden (not released), locked (released but not enabled), and
  // active (enabled). LLM_ENABLE_ROUTINE_APPROVALS gates the whole section; the two agent-approval
  // configs drive release vs enable. The section is also withheld until the trigger config is
  // complete, so agent mode is chosen last.
  const {llmEnableRoutineApprovals, llmEnableRoutineConfigAuthorship, llmEnableAgentMemory} =
    useRolloutFlags();
  const authorshipEnabled = llmEnableRoutineConfigAuthorship === true;
  const fullAccessEnabled = BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS;
  // Enabling implies release, so treat an enabled tenant as visible even if the release flag lags.
  const agentApprovalsVisible = BenchlingConfig.LLM_AGENT_APPROVALS_RELEASED || fullAccessEnabled;
  const showAccessSection =
    Boolean(llmEnableRoutineApprovals) &&
    agentApprovalsVisible &&
    isTriggerComplete(formState, authorshipEnabled);

  const isTimeBased = formState.triggerType === TriggerType.TIME_BASED;
  const writeDisabled = isEdit && !canEdit;

  const canSave = canSaveRoutine({
    formState,
    isEdit,
    isDirty,
    isSaving,
    authorshipEnabled,
    isUploading,
  });

  return (
    <div className={classes.form}>
      <Stack spacing="md">
        <Stack spacing={4}>
          <Title order={4}>{isEdit ? 'Edit Agent' : 'Create new Agent'}</Title>
          <Text size="md" c="dimmed">
            When this Agent runs, it starts a new chat in your chat list — just like starting a chat yourself.
            You will also receive an email notification when it's complete if you have email notifications on.
          </Text>
        </Stack>

        <Box component="fieldset" className={classes.fieldset} disabled={writeDisabled}>
          <Stack spacing="md">
            <TextInput
              label="Name"
              value={formState.name}
              onChange={(e) => {
                onNameChange(e.currentTarget.value);
              }}
            />

            <Textarea
              label="Description"
              description="A summary of what this Agent does — this will not be sent along to the agent."
              placeholder="optional"
              minRows={2}
              autosize={true}
              maxRows={8}
              value={formState.description}
              onChange={(e) => {
                onDescriptionChange(e.currentTarget.value);
              }}
            />

            <Textarea
              label="Instructions"
              description="The prompt that will be sent to the agent when this Agent runs."
              minRows={4}
              autosize={true}
              maxRows={16}
              value={formState.prompt}
              onChange={(e) => {
                onPromptChange(e.currentTarget.value);
              }}
            />

            <Stack spacing="xs">
              <RoutineFieldHeader
                title="Attachments"
                description="Attach files to provide additional context to the agent when this Agent runs."
              />
              {formState.fileBlocks.length > 0 && (
                <Group spacing="xs">
                  {formState.fileBlocks.map((fileBlock, index) => (
                    <Badge
                      key={`${fileBlock.external_file_id ?? fileBlock.s3_location}-${index}`}
                      variant="outline"
                      size="sm"
                      rightSection={
                        <CloseButton
                          aria-label={`Remove ${fileBlock.filename}`}
                          size="xs"
                          onClick={() => {
                            onRemoveFileBlock(index);
                          }}
                        />
                      }
                    >
                      {fileBlock.filename}
                    </Badge>
                  ))}
                </Group>
              )}
              <Group>
                <FileButton
                  resetRef={resetFileInputRef}
                  onChange={(file) => {
                    if (file != null) {
                      onAddFile(file);
                    }
                    // Reset so selecting the same file again re-triggers onChange.
                    resetFileInputRef.current?.();
                  }}
                >
                  {(props) => (
                    <Button
                      {...props}
                      variant="secondary"
                      size="sm"
                      disabled={isUploading}
                      loading={isUploading}
                    >
                      + Add file
                    </Button>
                  )}
                </FileButton>
              </Group>
            </Stack>

            {llmEnableAgentMemory === true && (
              <>
                <Divider />
                <Switch
                  aria-label="Enable Agent memory"
                  label="Memory"
                  description="Let this Agent recall and update durable knowledge across separate runs."
                  checked={formState.memoryEnabled}
                  onChange={(event) => {
                    onMemoryEnabledChange(event.currentTarget.checked);
                  }}
                />
              </>
            )}

            <Divider />

            <TriggerTypeSelector
              value={formState.triggerType}
              onChange={onTriggerTypeChange}
              timeBasedEnabled={timeBasedEnabled}
              eventBasedEnabled={eventBasedEnabled}
              disabled={isEdit || writeDisabled}
            />

            {formState.triggerType != null &&
              (isTimeBased ? (
                <TimeTriggerConfigForm
                  config={formState.triggerConfig as Partial<TimeTriggerConfig>}
                  onChange={onTriggerConfigChange}
                />
              ) : (
                <EventTriggerConfigForm
                  config={formState.triggerConfig as Partial<EventTriggerConfig>}
                  onChange={onTriggerConfigChange}
                  allowedTriggers={allowedEventTriggers}
                />
              ))}

            {!isEdit && BenchlingConfig.LLM_ENABLE_ROUTINES_RUN_AS_SERVICE_ACCOUNT && (
              <>
                <Divider />
                <Stack spacing="xs">
                  <RoutineFieldHeader
                    title="Agent identity"
                    description="Provision a dedicated service account for this Agent."
                  />
                  <Checkbox
                    label="Run as this Agent"
                    description="Create a service account for this Agent at save time. This cannot be changed later."
                    checked={formState.runAs === RoutineRunAs.SERVICE_ACCOUNT}
                    onChange={(event) => {
                      onRunAsChange(
                        event.currentTarget.checked ? RoutineRunAs.SERVICE_ACCOUNT : RoutineRunAs.CREATOR
                      );
                    }}
                  />
                </Stack>
              </>
            )}

            {showAccessSection && (
              <>
                <Divider />
                <RoutineAccessSection
                  allowedApprovalCategories={formState.allowedApprovalCategories}
                  agentWorkspaceFolderId={formState.agentWorkspaceFolderId}
                  fullAccessEnabled={fullAccessEnabled}
                  onAccessLevelChange={onAccessLevelChange}
                  onAgentWorkspaceFolderChange={onAgentWorkspaceFolderChange}
                />
              </>
            )}

            {error != null && (
              <Alert color="red" variant="light">
                {error}
              </Alert>
            )}
          </Stack>
        </Box>

        <Group position="apart" pt="md">
          <Group spacing="xs">
            {canArchive && onArchive && (
              <Button variant="warn" size="sm" onClick={openArchiveModal} disabled={isSaving}>
                Archive
              </Button>
            )}
            <Group spacing="xs">
              {isEdit && currentStatus === RoutineStatus.ACTIVE && onPause && (
                <Button variant="secondary" size="sm" onClick={onPause} disabled={isSaving || !canPause}>
                  Pause
                </Button>
              )}
              {isEdit && currentStatus === RoutineStatus.PAUSED && onResume && (
                <Button variant="secondary" size="sm" onClick={onResume} disabled={isSaving || !canResume}>
                  Resume
                </Button>
              )}
              {isEdit && onViewAuditLog && (
                <Button variant="ghost" size="sm" onClick={onViewAuditLog}>
                  {EXPORT_AUDIT_LOG_DISPLAY_STRING}
                </Button>
              )}
            </Group>
          </Group>
          <Group spacing="xs">
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
              {!writeDisabled ? 'Cancel' : 'Back'}
            </Button>
            {!writeDisabled && (
              <Button variant="primary" size="sm" onClick={onSave} disabled={!canSave} loading={isSaving}>
                Save
              </Button>
            )}
          </Group>
        </Group>
        <Modal
          opened={archiveModalOpened}
          onClose={closeArchiveModal}
          title="Archive Agent"
          centered={true}
          closeOnClickOutside={true}
          zIndex={ZINDEX_FLOATING_UI}
        >
          <Stack spacing="md">
            <Text size="sm">Are you sure? This will stop and remove this Agent.</Text>
            <Group position="right" spacing="xs">
              <Button variant="ghost" size="sm" onClick={closeArchiveModal}>
                Cancel
              </Button>
              <Button
                variant="warn"
                size="sm"
                onClick={() => {
                  closeArchiveModal();
                  onArchive?.();
                }}
              >
                Archive
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </div>
  );
}
